import { useContext, useMemo, useState } from 'react';

import { Box, Typography } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { SxProps, Theme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';

import { useFeatureFlags, useLanguage } from '../../hooks';
import { SkipLogicQuestion } from '../../types';
import { NumberQuestionBase } from '../../types/questions';
import { resolveMeterLink } from '../../utils/meterLink';
import { InfoAlert } from '../uiComponents/infoAlert';
import { FormBuilderContext } from './FormBuilderProvider';

const customStyles = {
  comparisonOptionLabel: {
    fontSize: '14px',
    lineHeight: '20px',
  },
  comparisonOptionSecondary: {
    fontSize: '12px',
    lineHeight: '16px',
    color: '#6B7280',
    whiteSpace: 'normal',
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    my: 1,
  },
  fieldContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  label: {
    fontSize: 14,
    mb: 1,
  },
  select: {
    '& .MuiSelect-select': {
      fontSize: 14,
    },
  },
  inputsRow: {
    display: 'flex',
    gap: 2,
    alignItems: 'center',
  },
  textField: {
    '& .MuiInputBase-input': {
      fontSize: 14,
    },
  },
  andLabel: {
    fontSize: 14,
  },
  questionSelect: {
    '& .MuiSelect-select': {
      fontSize: 14,
    },
  },
  errorMessage: {
    color: 'error.main',
    fontSize: 12,
    mt: 1,
  },
  placeholderText: {
    color: 'text.secondary',
  },
} satisfies Record<string, SxProps<Theme>>;

type comparisonType =
  | 'greater'
  | 'greaterOrEqual'
  | 'less'
  | 'lessOrEqual'
  | 'between'
  | 'notBetween'
  | undefined;

type relativeComparisonType = comparisonType | 'betweenQuestionOffset' | 'withinMargin';

type validationType = 'noValidation' | 'numberValidation' | 'numberValidationRelative';

interface NumberValidationProps {
  questionId: string;
  upperValue: string;
  lowerValue: string;
  numberValidation: comparisonType;
  numberValidationRelative?: relativeComparisonType;
  validationOffset?: string;
  /** 'withinMargin' only: how far below the last reading a new one may go. Empty means 0. */
  lowerMargin?: string;
  /** 'withinMargin' only: largest accepted jump above the last reading. Empty means no cap. */
  upperMargin?: string;
  /** Meter link stored on the question. On its own it does not prove the link is live. */
  associatedMetadata?: NumberQuestionBase['associatedMetadata'];
  logicQuestions?: SkipLogicQuestion[];
  onNumberValidationChange: (
    questionId: string,
    action: string,
    value: string,
    tableId?: string,
  ) => void;
  tableId?: string;
}

/**
 * Identity of the meter link, for use as the panel's `key` at its call sites.
 *
 * The panel derives `typeOfValidation` and `comparisonType` from props once, at mount, and there
 * is no effect re-deriving them. Unlinking the meter deletes the comparator and both margins from
 * the question, so without a remount the panel goes on showing a configuration that no longer
 * exists. It lives here, next to the state it protects, so both call sites key on the same thing.
 */
export const numberValidationPanelKey = (associatedMetadata?: { meterId?: string }) =>
  `number-validation-${associatedMetadata?.meterId ?? 'unlinked'}`;

const NumberValidation = (props: NumberValidationProps) => {
  const {
    questionId,
    upperValue,
    lowerValue,
    numberValidation,
    validationOffset = '0',
    logicQuestions = [],
    onNumberValidationChange,
    tableId,
    numberValidationRelative,
    lowerMargin,
    upperMargin,
    associatedMetadata,
  } = props;
  const [typeOfValidation, setTypeOfValidation] = useState(
    numberValidation
      ? 'numberValidation'
      : numberValidationRelative
      ? 'numberValidationRelative'
      : 'noValidation',
  );
  const [comparisonType, setComparisonType] = useState(
    numberValidation ?? numberValidationRelative ?? '',
  );

  const lang = useLanguage();
  const { checkFlag } = useFeatureFlags();
  const isRelativeRangeEnabled = checkFlag('NumberRelativeRangeValidation');
  // Its own flag rather than riding on the relative-range one: turning that off would strip
  // every relative comparator, including the question-based ones customers already use in the
  // app, so it cannot serve as the rollback switch for this comparator alone.
  const isWithinMarginEnabled = checkFlag('NumberWithinMarginValidation');

  const questionsToCompare = logicQuestions.filter(
    (question) => question.type === 'number' && question.id !== questionId,
  );

  // The link is derived from the full predicate, not from associatedMetadata alone, because
  // the two sides can drift apart (see resolveMeterLink). Deriving it on every render is also
  // what makes the lost-link state recover on its own once the link is restored.
  const { questions: formQuestions } = useContext(FormBuilderContext);
  const meterLink = useMemo(
    () =>
      resolveMeterLink(
        { id: questionId, type: 'number', associatedMetadata } as NumberQuestionBase,
        formQuestions ?? [],
      ),
    [questionId, associatedMetadata, formQuestions],
  );
  // Wiring and availability are two different questions, and collapsing them produced the wrong
  // copy: `isLinkedToMeter` says whether the link is intact, `isComparatorAvailable` whether the
  // author can pick the comparator here. Inside a repeatable group the link may be perfectly
  // intact and the comparator still unavailable, because answering there never updates a meter,
  // so the last reading never moves. The repeatable-group rule itself lives in the predicate
  // (check 6), not in this prop: `handleCombine` can move an already-configured question into a
  // table, and a rule read from `tableId` would leave that configuration evaluated by every
  // surface that consults the predicate instead of the prop.
  const isInsideRepeatableGroup = tableId != null;
  const isLinkedToMeter = meterLink != null;
  const isComparatorAvailable = isLinkedToMeter && !isInsideRepeatableGroup;
  const isWithinMargin = comparisonType === 'withinMargin';
  // With the flag off, a saved configuration stays listed and readable but must not accept
  // edits: AC-11 promises it goes inert, and an editable field lets an author accumulate changes
  // during the rollback window that take effect the moment the flag comes back on.
  const isWithinMarginInert = isWithinMargin && !isWithinMarginEnabled;
  // Values survive a lost link so nothing is silently dropped, but the rule cannot run. Which
  // notice to show depends on WHY it cannot run: inside a group, relinking does not help, so
  // promising that it will is false.
  const showLostLinkWarning = isWithinMargin && !isComparatorAvailable;
  const unavailableReason = isInsideRepeatableGroup
    ? lang.form_builder.withinMarginNotInRepeatable
    : lang.form_builder.withinMarginUnlinkedWarning;

  const validationTypes: { value: validationType; label: string }[] = [
    { value: 'noValidation', label: lang.form_builder.noValidation },
    { value: 'numberValidation', label: lang.form_builder.validationFixedRange },
    ...(isRelativeRangeEnabled
      ? [
          {
            value: 'numberValidationRelative' as validationType,
            label: lang.form_builder.validationRelativeRange,
          },
        ]
      : []),
  ];

  const comparisonTypes = [
    { value: 'greater', label: lang.form_builder.greater },
    { value: 'greaterOrEqual', label: lang.form_builder.greaterOrEqual },
    { value: 'less', label: lang.form_builder.less },
    { value: 'lessOrEqual', label: lang.form_builder.lessOrEqual },
    { value: 'between', label: lang.form_builder.between },
    { value: 'notBetween', label: lang.form_builder.notBetween },
    ...(typeOfValidation === 'numberValidationRelative'
      ? [
          { value: 'betweenQuestionOffset', label: lang.form_builder.betweenQuestionOffset },
          {
            value: 'withinMargin',
            label: lang.form_builder.withinMargin,
            // Kept listed when a saved question already uses it, so turning the flag off
            // leaves the configuration inert instead of blanking the dropdown.
            hidden:
              (!isWithinMarginEnabled || isInsideRepeatableGroup) &&
              comparisonType !== 'withinMargin',
            disabled: !isComparatorAvailable,
            secondary: isInsideRepeatableGroup
              ? lang.form_builder.withinMarginNotInRepeatable
              : !isLinkedToMeter
              ? lang.form_builder.withinMarginRequiresMeter
              : undefined,
          },
        ]
      : []),
  ];

  const validationMessage = () => {
    const offsetDisplay =
      validationOffset && validationOffset !== '0'
        ? comparisonType === 'betweenQuestionOffset'
          ? ''
          : Number(validationOffset) > 0
          ? ` ${lang.form_builder.plus} ${Math.abs(Number(validationOffset))}`
          : ` ${lang.form_builder.minus} ${Math.abs(Number(validationOffset))}`
        : '';
    let valueLabel = '';
    switch (comparisonType) {
      case 'greater':
      case 'greaterOrEqual':
        valueLabel = lowerValue;
        break;
      case 'less':
      case 'lessOrEqual':
        valueLabel = upperValue;
        break;
      case 'between':
      case 'notBetween':
        valueLabel = `${lowerValue} ${lang.form_builder.and} ${upperValue}`;
        break;
    }
    const comparisonTypeLabel = comparisonTypes
      .find((ct) => ct.value === comparisonType)
      ?.label?.toLowerCase();
    if (typeOfValidation === 'numberValidation') {
      if (comparisonType === 'notBetween') {
        return `${
          lang.form_builder.theNumberMustNotBe
        } ${lang.form_builder.between.toLowerCase()} ${valueLabel}`;
      }
      return `${lang.form_builder.theNumberMustBe} ${comparisonTypeLabel} ${valueLabel}`;
    }

    const getLowerQuestionName = () => {
      const question = questionsToCompare.find((q) => q.id === lowerValue);
      return question?.name || '';
    };
    const getUpperQuestionName = () => {
      const question = questionsToCompare.find((q) => q.id === upperValue);
      return question?.name || '';
    };
    const offsetSign =
      Number(validationOffset) > 0
        ? lang.form_builder.plus
        : Number(validationOffset) === 0
        ? ''
        : lang.form_builder.minus;

    switch (comparisonType) {
      case 'greater':
      case 'greaterOrEqual':
        valueLabel = getLowerQuestionName();
        break;
      case 'less':
      case 'lessOrEqual':
        valueLabel = getUpperQuestionName();
        break;
      case 'between':
      case 'notBetween':
        valueLabel = `${getLowerQuestionName()} ${lang.form_builder.and} ${getUpperQuestionName()}`;
        break;
    }
    if (comparisonType === 'notBetween') {
      return `${
        lang.form_builder.theNumberMustNotBe
      } ${lang.form_builder.between.toLowerCase()} ${valueLabel}`;
    }
    if (comparisonType === 'betweenQuestionOffset') {
      const lowerQuestionName = getLowerQuestionName();
      const offsetLabel = validationOffset !== '0' ? Math.abs(Number(validationOffset)) : '';
      if (lowerQuestionName === '') {
        return lang.form_builder.theNumberMustBe;
      }
      return `${
        lang.form_builder.theNumberMustBe
      } ${lang.form_builder.between.toLowerCase()} ${lowerQuestionName} ${
        lang.form_builder.and
      } ${lowerQuestionName} ${offsetSign} ${offsetLabel}`;
    }
    if (comparisonType === 'withinMargin') {
      // Described against the meter, never against a reference question: there is no question
      // to name here, and the previous branches would render an empty sentence.
      const back = Number(lowerMargin || 0);
      const hasForward = upperMargin != null && upperMargin !== '';
      const forward = hasForward ? Number(upperMargin) : null;
      // A margin of zero is omitted from the sentence rather than rendered as "- 0" or "+ 0":
      // zero displacement is better read as "the last reading" than as arithmetic on it.
      const lowerLabel = back
        ? `${lang.form_builder.withinMarginLastReading} ${lang.form_builder.minus} ${back}`
        : lang.form_builder.withinMarginLastReading;
      const upperLabel = forward
        ? `${lang.form_builder.withinMarginLastReading} ${lang.form_builder.plus} ${forward}`
        : lang.form_builder.withinMarginLastReading;
      if (!hasForward) {
        return `${
          lang.form_builder.theNumberMustBe
        } ${lang.form_builder.greaterOrEqual.toLowerCase()} ${lowerLabel}`;
      }
      // A collapsed window (both margins zero) is a legitimate configuration: it expresses
      // "confirm the current reading without allowing movement". Describing it as a range
      // would print the same value twice, and describing it as a minimum would be false,
      // since anything above it is rejected too.
      if (back === 0 && forward === 0) {
        return `${lang.form_builder.theNumberMustBe} ${lang.form_builder.withinMarginExactly} ${lang.form_builder.withinMarginLastReading}`;
      }
      return `${
        lang.form_builder.theNumberMustBe
      } ${lang.form_builder.between.toLowerCase()} ${lowerLabel} ${
        lang.form_builder.and
      } ${upperLabel}`;
    }
    if (typeOfValidation === 'numberValidationRelative') {
      return `${lang.form_builder.theNumberMustBe} ${comparisonTypeLabel} ${valueLabel} ${offsetDisplay}`;
    }
    return '';
  };

  const handleComparisonTypeChange = (type: string, value: comparisonType | '') => {
    setComparisonType(value);
    if (value === '') return;
    onNumberValidationChange(questionId, type, value, tableId);
  };

  const handleTypeOfValidationChange = (value: validationType) => {
    const oldTypeOfValidation = typeOfValidation;
    setTypeOfValidation(value);
    if (!value) return;
    if (oldTypeOfValidation !== value) {
      setComparisonType('');
    }
    if (value === 'noValidation') {
      onNumberValidationChange(questionId, 'numberValidation', 'noValidation', tableId);
      return;
    }
    onNumberValidationChange(
      questionId,
      value,
      oldTypeOfValidation !== value ? '' : comparisonType,
      tableId,
    );
  };

  /**
   * Both the fixed-range and the relative-range dropdowns list comparators, and they used to map
   * the options independently. That is how the disabled state, the secondary explanation and the
   * flag gate for `withinMargin` ended up applied to the dropdown that never lists it. One
   * renderer for both means a field added to an option cannot be honoured by one and ignored by
   * the other.
   *
   * Only the new option carries a `data-testid`: adding one to the six pre-existing comparators
   * would publish a test hook nobody asked for.
   *
   * An option with nothing extra to say renders its bare label rather than a wrapped one. MUI
   * renders the selected item's children inside the collapsed input, so wrapping every option
   * would change the metrics of every pre-existing comparator field.
   */
  const renderComparisonOptions = () =>
    comparisonTypes
      .filter((option) => !option.hidden)
      .map((option) => (
        <MenuItem
          key={option.value}
          value={option.value}
          disabled={option.disabled}
          data-testid={
            option.value === 'withinMargin' ? 'number-validation-within-margin' : undefined
          }
        >
          {option.secondary ? (
            <Box>
              <Typography sx={customStyles.comparisonOptionLabel}>{option.label}</Typography>
              <Typography sx={customStyles.comparisonOptionSecondary}>
                {option.secondary}
              </Typography>
            </Box>
          ) : (
            option.label
          )}
        </MenuItem>
      ));

  /**
   * The collapsed field shows the bare label, never the secondary line.
   *
   * MUI renders the selected item's children inside the closed input, so the explanation written
   * for the menu leaked out of it and sat under the field for good. That put the same sentence in
   * two places at once - the notice below the field already says the link is missing - and it said
   * it as a permanent property of the chosen comparator rather than as a reason for a choice being
   * made. `renderValue` keeps the explanation where it helps: next to the option, while picking.
   */
  const renderComparisonValue = (selected: unknown) => {
    if (!selected) {
      return (
        <Typography sx={customStyles.placeholderText}>
          {lang.form_builder.validationSelectComparisonType}
        </Typography>
      );
    }
    return comparisonTypes.find((option) => option.value === selected)?.label ?? String(selected);
  };

  const renderFixedRangeFields = () => {
    const isLessValidation = [
      'less',
      'lessOrEqual',
      'between',
      'notBetween',
      'betweenQuestionOffset',
    ].includes(comparisonType);
    const isGreaterValidation = ['greater', 'greaterOrEqual', 'between', 'notBetween'].includes(
      comparisonType,
    );
    const isBetweenValidation = ['between', 'notBetween'].includes(comparisonType);

    const isEmptySet = () => {
      if (isBetweenValidation && !isNaN(parseFloat(lowerValue)) && !isNaN(parseFloat(upperValue))) {
        return parseFloat(lowerValue) >= parseFloat(upperValue);
      }
      return false;
    };

    return (
      <>
        <Box sx={customStyles.fieldContainer}>
          <Typography sx={customStyles.label}>
            {lang.form_builder.validationComparisonType}
          </Typography>
          <Select
            value={comparisonType}
            size='small'
            fullWidth
            sx={customStyles.select}
            onChange={(e) =>
              handleComparisonTypeChange('numberValidation', e.target.value as comparisonType)
            }
            displayEmpty
            renderValue={renderComparisonValue}
          >
            <MenuItem
              value=''
              disabled
            >
              <Typography sx={customStyles.placeholderText}>
                {lang.form_builder.validationSelectComparisonType}
              </Typography>
            </MenuItem>
            {renderComparisonOptions()}
          </Select>
        </Box>

        {comparisonType && (
          <Box sx={customStyles.inputsRow}>
            {(isGreaterValidation || isBetweenValidation) && (
              <TextField
                label={lang.form_builder.minimumValue}
                value={lowerValue || ''}
                size='small'
                type='number'
                sx={customStyles.textField}
                onChange={(e) => {
                  const newValue = e.target.value;
                  if (newValue === '' || !isNaN(Number(newValue))) {
                    onNumberValidationChange(questionId, 'lowerValue', newValue, tableId);
                  }
                }}
                error={isNaN(parseFloat(lowerValue)) || isEmptySet()}
                helperText={
                  isNaN(parseFloat(lowerValue))
                    ? lang.form_builder.validNumberWarning
                    : isEmptySet()
                    ? lang.form_builder.validSetWarning
                    : ''
                }
              />
            )}
            {isBetweenValidation && (
              <Typography sx={customStyles.andLabel}>{lang.form_builder.and}</Typography>
            )}
            {(isLessValidation || isBetweenValidation) && (
              <TextField
                label={lang.form_builder.maximumValue}
                value={upperValue || ''}
                size='small'
                type='number'
                sx={customStyles.textField}
                onChange={(e) => {
                  const newValue = e.target.value;
                  if (newValue === '' || !isNaN(Number(newValue))) {
                    onNumberValidationChange(questionId, 'upperValue', newValue, tableId);
                  }
                }}
                error={isNaN(parseFloat(upperValue)) || isEmptySet()}
                helperText={
                  isNaN(parseFloat(upperValue))
                    ? lang.form_builder.validNumberWarning
                    : isEmptySet()
                    ? lang.form_builder.validSetWarning
                    : ''
                }
              />
            )}
          </Box>
        )}
      </>
    );
  };

  const renderQuestionSelect = (
    label: string,
    value: string | undefined,
    onChange: (value: string) => void,
  ) => {
    if (!questionsToCompare?.length) {
      return (
        <Box sx={customStyles.fieldContainer}>
          <Typography sx={customStyles.label}>{label}</Typography>
          <Select
            value=''
            size='small'
            fullWidth
            disabled
            sx={customStyles.questionSelect}
            displayEmpty
          >
            <MenuItem
              value=''
              disabled
            >
              <Typography sx={customStyles.placeholderText}>
                {lang.form_builder.pickQuestion}
              </Typography>
            </MenuItem>
          </Select>
          <Typography sx={customStyles.errorMessage}>
            {lang.form_builder.noQuestionsAvailable}
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={customStyles.fieldContainer}>
        <Typography sx={customStyles.label}>{label}</Typography>
        <Select
          value={value ?? ''}
          size='small'
          fullWidth
          sx={customStyles.questionSelect}
          onChange={(e) => onChange(e.target.value)}
          displayEmpty
        >
          <MenuItem
            value=''
            disabled
          >
            <Typography sx={customStyles.placeholderText}>
              {lang.form_builder.pickQuestion}
            </Typography>
          </MenuItem>
          {questionsToCompare.map((question) => (
            <MenuItem
              key={question.id}
              value={question.id}
            >
              {question.name}
            </MenuItem>
          ))}
        </Select>
      </Box>
    );
  };

  /**
   * Margins are free-form and start empty on purpose: an empty forward margin means "no upper
   * cap", while '0' is a real cap pinned at the last reading. Pre-filling either would put
   * words in the author's mouth. Negative values are dropped on the way in rather than saved
   * and flagged, matching how the offset field already rejects non-numeric input.
   */
  const renderMarginField = (
    label: string,
    key: 'lowerMargin' | 'upperMargin',
    value: string | undefined,
    testId: string,
  ) => (
    <Box sx={customStyles.fieldContainer}>
      <Typography sx={customStyles.label}>{label}</Typography>
      <TextField
        type='number'
        size='small'
        fullWidth
        value={value ?? ''}
        disabled={isWithinMarginInert}
        sx={customStyles.textField}
        inputProps={{ 'data-testid': testId, min: 0 }}
        onChange={(e) => {
          const newValue = e.target.value;
          if (newValue !== '' && (isNaN(Number(newValue)) || Number(newValue) < 0)) return;
          onNumberValidationChange(questionId, key, newValue, tableId);
        }}
      />
    </Box>
  );

  const renderRelativeRangeFields = () => {
    const isBetweenValidation = ['between', 'notBetween'].includes(comparisonType);
    const isGreaterOffsetValidation = comparisonType === 'betweenQuestionOffset';
    return (
      <>
        <Box sx={customStyles.fieldContainer}>
          <Typography sx={customStyles.label}>
            {lang.form_builder.validationComparisonType}
          </Typography>
          <Select
            value={comparisonType}
            size='small'
            fullWidth
            // Disabled along with the margins while the configuration is inert. Leaving it
            // enabled made the ONE edit still reachable the destructive one: changing the
            // comparator runs the cleanup, which drops both margins, and with the flag off the
            // option is hidden so the author cannot pick it back. The rollback window would
            // destroy exactly the configuration it exists to preserve.
            disabled={isWithinMarginInert}
            sx={customStyles.select}
            onChange={(e) =>
              handleComparisonTypeChange(
                'numberValidationRelative',
                e.target.value as comparisonType,
              )
            }
            displayEmpty
            renderValue={renderComparisonValue}
          >
            <MenuItem
              value=''
              disabled
            >
              <Typography sx={customStyles.placeholderText}>
                {lang.form_builder.validationSelectComparisonType}
              </Typography>
            </MenuItem>
            {renderComparisonOptions()}
          </Select>
        </Box>

        {isWithinMargin && (
          <Box sx={customStyles.fieldContainer}>
            <Box sx={customStyles.inputsRow}>
              {renderMarginField(
                lang.form_builder.withinMarginBack,
                'lowerMargin',
                lowerMargin,
                'number-validation-lower-margin',
              )}
              {renderMarginField(
                lang.form_builder.withinMarginForward,
                'upperMargin',
                upperMargin,
                'number-validation-upper-margin',
              )}
            </Box>
          </Box>
        )}

        {comparisonType && !isWithinMargin && (
          <Box sx={customStyles.fieldContainer}>
            <Box sx={customStyles.inputsRow}>
              {(isBetweenValidation ||
                ['greater', 'greaterOrEqual'].includes(comparisonType) ||
                isGreaterOffsetValidation) &&
                renderQuestionSelect(lang.form_builder.minimumValue, lowerValue, (value) =>
                  onNumberValidationChange(questionId, 'lowerValue', value, tableId),
                )}

              {isBetweenValidation && (
                <Typography sx={customStyles.andLabel}>{lang.form_builder.and}</Typography>
              )}

              {(isBetweenValidation || ['less', 'lessOrEqual'].includes(comparisonType)) &&
                renderQuestionSelect(lang.form_builder.maximumValue, upperValue, (value) =>
                  onNumberValidationChange(questionId, 'upperValue', value, tableId),
                )}
            </Box>

            {!isBetweenValidation && (
              <Box sx={customStyles.fieldContainer}>
                <Typography sx={customStyles.label}>{lang.form_builder.offset}</Typography>
                <TextField
                  type='number'
                  size='small'
                  fullWidth
                  value={validationOffset || '0'}
                  sx={customStyles.textField}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    if (newValue === '' || !isNaN(Number(newValue))) {
                      onNumberValidationChange(questionId, 'validationOffset', newValue, tableId);
                    }
                  }}
                />
              </Box>
            )}
          </Box>
        )}
      </>
    );
  };

  return (
    <Box sx={customStyles.container}>
      <Box sx={customStyles.fieldContainer}>
        <Typography sx={customStyles.label}>{lang.form_builder.validationType}</Typography>
        <Select
          value={typeOfValidation}
          size='small'
          fullWidth
          // Disabled with the other two while the configuration is inert. It was the last control
          // the author could still reach with the flag off, and the only destructive one: changing
          // the type sets the comparator to '', which runs the cleanup over both margins AND makes
          // the `hidden` guard drop the option from the dropdown, so it cannot be chosen back. The
          // rollback window destroyed the configuration it exists to preserve.
          disabled={isWithinMarginInert}
          sx={customStyles.select}
          onChange={(e) => handleTypeOfValidationChange(e.target.value as validationType)}
        >
          {validationTypes.map((type) => (
            <MenuItem
              key={type.value}
              value={type.value}
            >
              {type.label}
            </MenuItem>
          ))}
        </Select>
      </Box>
      {typeOfValidation === 'numberValidation' && renderFixedRangeFields()}
      {typeOfValidation === 'numberValidationRelative' && renderRelativeRangeFields()}

      {showLostLinkWarning || isWithinMarginInert ? (
        <InfoAlert
          severity='warning'
          testId='number-validation-rule-notice'
          message={isWithinMarginInert ? lang.form_builder.withinMarginInert : unavailableReason}
        />
      ) : (
        comparisonType &&
        typeOfValidation !== 'noValidation' && (
          <InfoAlert
            testId='number-validation-rule-notice'
            message={validationMessage()}
          />
        )
      )}
    </Box>
  );
};

export { NumberValidation };
